import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { Loader2, FileCheck, Download } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { publicReportEndpoints } from '@/lib/api/endpoints';
import { formatDateTimeInTimeZone, formatTimeZoneLabel } from '@/lib/date-values';
import { formatDateOnly } from '@/lib/dates/date-only';
import { CapturedSignature, DocRenderer } from '@/features/reports/public/DocRenderer';
import { documentDepositLabel } from '@/lib/sales/document-deposit-label';
import type { HostedReportView, PublicEstimateSummary } from '@/lib/api/types/reports';
import { publicReportPdfUrl } from '@/lib/reports/publicReportLinks';
import { PublicCenteredPage } from '@/components/public/PublicCenteredPage';

// Combined report + estimate accept page (spec §9.3 / Appendix B.3). When a
// published Report spawned an Estimate, the customer sees the full report
// content (read-only, rendered by DocRenderer) followed by the
// recommended-work summary and a single "Approve and schedule" CTA that hands
// off to the standard Estimate accept flow (/estimate/$id) — no parallel
// acceptance logic lives here. The backend 404s when no estimate was spawned.

export const Route = createFileRoute('/(public)/combined-doc/$token')({
  component: CombinedDocPage,
  validateSearch: (search) => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
});

function money(v: number | string | null | undefined): string {
  const n = Number(v ?? 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number.isFinite(n) ? n : 0,
  );
}

/**
 * The one line on this page that tells the customer what they owe up front.
 *
 * It used to print `depositValue`, which is a PERCENT or a dollar figure
 * depending on `depositType` — so a 50% deposit read "$50.00" whatever the
 * estimate was worth. The money now comes from the server-computed
 * `depositAmount` (the deposit engine, against the deposit-eligible total), and
 * the percentage — a term of the offer, not a derived number — is stated in the
 * label beside it.
 *
 * Exported so the numbers can be asserted without mounting the route.
 */
export function depositDueLine(
  estimate: Pick<PublicEstimateSummary, 'depositType' | 'depositValue' | 'depositAmount'>,
): string {
  const label = documentDepositLabel({
    depositType: estimate.depositType,
    depositValue: estimate.depositValue,
    label: 'Deposit due on approval',
  });
  return `${label}: ${money(estimate.depositAmount)}`;
}

/**
 * Whether this document quotes a deposit at all.
 *
 * Keyed on the server-computed amount, NOT on `depositRequired`. That flag is
 * the scheduling policy ("require this deposit before the job is scheduled"),
 * it defaults off, and nothing that charges the deposit reads it. Gating the
 * row on it hid a quoted deposit from the customer on this surface while the
 * estimate PDF, the quote page and the proposal all printed it.
 *
 * Exported so the branch can be asserted without mounting the route.
 */
export function hasQuotedDeposit(estimate: Pick<PublicEstimateSummary, 'depositAmount'>): boolean {
  const amount = Number(estimate.depositAmount ?? 0);
  return Number.isFinite(amount) && amount > 0;
}

// validUntil is a date-only (@db.Date) field — tz-less, so parse at local
// midnight and format as a plain date rather than through the instant formatter
// (which would shift it a day depending on the viewer's offset).
// The formatting itself lives in `@/lib/dates/date-only`, the one
// implementation every surface shares; this only adds the null placeholder.
function fmtDateOnly(value: string | null | undefined): string | null {
  return value ? formatDateOnly(value) : null;
}

function CombinedDocPage() {
  const { token } = Route.useParams();
  const search = Route.useSearch();

  const { data, isLoading, error } = useApiQuery<HostedReportView>({
    queryKey: ['combined-doc', token, search.email ?? null],
    queryFn: async () =>
      apiGet(operationsClient, publicReportEndpoints.combinedDoc(token), {
        params: search.email ? { email: search.email } : undefined,
      }),
    showErrorToast: false,
  });

  // Beacon: log access on mount, same as the hosted report view.
  useEffect(() => {
    if (!data) return;
    void apiPost(operationsClient, publicReportEndpoints.accessLog(token), {
      email: search.email,
    } as never);
  }, [data, token, search.email]);

  const styleVars = useMemo(() => {
    if (!data) return {} as React.CSSProperties;
    const { primaryColor, secondaryColor, bodyFont, headingFont } = data.branding;
    return {
      ['--rpt-primary' as never]: primaryColor,
      ['--rpt-secondary' as never]: secondaryColor,
      ['--rpt-body-font' as never]: `"${bodyFont}", system-ui, sans-serif`,
      ['--rpt-heading-font' as never]: `"${headingFont}", system-ui, sans-serif`,
    } as React.CSSProperties;
  }, [data]);

  if (isLoading) {
    return (
      <PublicCenteredPage>
        <Loader2 className="h-6 w-6 animate-spin" />
      </PublicCenteredPage>
    );
  }
  // The backend 404s the combined view when the report never spawned an
  // estimate — treat a missing estimate on a 200 the same way.
  if (error || !data || !data.estimate) {
    return (
      <PublicCenteredPage className="bg-stone-50 text-center">
        <div className="max-w-md">
          <h1 className="mb-2 text-xl font-bold">Proposal unavailable</h1>
          <p className="text-muted-foreground">
            This report or its recommended estimate can’t be viewed right now. Please contact us for
            a fresh link.
          </p>
        </div>
      </PublicCenteredPage>
    );
  }

  const estimate = data.estimate;
  const tzLabel = formatTimeZoneLabel(data.timezone);
  const alreadyApproved = ['ACCEPTED', 'APPROVED', 'SIGNED'].includes(
    String(estimate.status).toUpperCase(),
  );
  const latestSignature = data.signatures[0] ?? null;

  return (
    // Own the vertical scroll: the app shell locks html/body/#root to
    // overflow:hidden, so a combined report+estimate taller than the viewport
    // would otherwise be unscrollable on the hosted view.
    <div
      className="h-dvh overflow-y-auto"
      style={{ ...styleVars, fontFamily: 'var(--rpt-body-font)', background: '#fafafa' }}
    >
      <main className="mx-auto my-8 max-w-3xl overflow-hidden rounded-lg bg-white shadow-sm">
        <header className="border-b-2 px-8 py-6" style={{ borderColor: 'var(--rpt-primary)' }}>
          {data.branding.logoUrl ? (
            <img
              src={data.branding.logoUrl}
              alt={data.branding.tenantName}
              className="mb-3 h-10 w-auto object-contain"
            />
          ) : null}
          <p className="font-mono text-xs text-muted-foreground">{data.report.number}</p>
          <h1
            className="mt-1 text-2xl font-bold"
            style={{ color: 'var(--rpt-primary)', fontFamily: 'var(--rpt-heading-font)' }}
          >
            {data.report.title}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {data.branding.tenantName}
            {data.report.publishedAt && (
              <>
                {' · '}
                {formatDateTimeInTimeZone(data.report.publishedAt, data.timezone ?? undefined, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </>
            )}
          </p>
          {tzLabel && <p className="text-xs text-muted-foreground">Times shown in {tzLabel}</p>}
        </header>

        {data.report.customerFacingIntro && (
          <section className="bg-stone-50 px-8 py-4 text-sm">
            {data.report.customerFacingIntro}
          </section>
        )}

        <div className="px-8 py-6">
          <DocRenderer
            sections={data.sections}
            photos={data.photos}
            signatureBlock={
              latestSignature ? (
                <CapturedSignature signature={latestSignature} timezone={data.timezone} />
              ) : undefined
            }
          />
        </div>

        {data.report.customerFacingOutro && (
          <section className="border-t px-8 py-4 text-sm">
            {data.report.customerFacingOutro}
          </section>
        )}

        {/* Recommended next steps — the estimate spawned by this report (§9.3). */}
        <section
          className="border-t-4 px-8 py-6"
          style={{ borderColor: 'var(--rpt-primary)' }}
          aria-labelledby="recommendation-heading"
        >
          <h2
            id="recommendation-heading"
            className="text-lg font-semibold"
            style={{ color: 'var(--rpt-primary)', fontFamily: 'var(--rpt-heading-font)' }}
          >
            Recommended next steps
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Estimate {estimate.estimateNumber}
            {estimate.lineCount > 0 &&
              ` · ${estimate.lineCount} item${estimate.lineCount === 1 ? '' : 's'}`}
          </p>

          <div className="mt-4 rounded-lg border bg-stone-50 p-5">
            <div className="flex items-end justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Total</p>
                <p className="text-3xl font-bold" style={{ color: 'var(--rpt-primary)' }}>
                  {money(estimate.grandTotal)}
                </p>
              </div>
              <dl className="text-right text-sm text-muted-foreground">
                <div className="flex justify-between gap-6">
                  <dt>Subtotal</dt>
                  <dd className="tabular-nums">{money(estimate.subtotal)}</dd>
                </div>
                <div className="flex justify-between gap-6">
                  <dt>Tax</dt>
                  <dd className="tabular-nums">{money(estimate.taxTotal)}</dd>
                </div>
              </dl>
            </div>

            {(hasQuotedDeposit(estimate) || estimate.validUntil) && (
              <ul className="mt-4 space-y-1 border-t pt-4 text-sm text-muted-foreground">
                {hasQuotedDeposit(estimate) && <li>{depositDueLine(estimate)}</li>}
                {fmtDateOnly(estimate.validUntil) && (
                  <li>Valid until {fmtDateOnly(estimate.validUntil)}</li>
                )}
              </ul>
            )}

            <div className="mt-5">
              {alreadyApproved ? (
                <p className="text-sm font-medium" style={{ color: 'var(--rpt-primary)' }}>
                  ✓ This estimate has already been approved. Thank you!
                </p>
              ) : (
                <Link
                  to="/estimate/$id"
                  params={{ id: estimate.id }}
                  search={{ deposit: undefined, session_id: undefined }}
                >
                  <Button size="lg" className="w-full sm:w-auto">
                    <FileCheck className="mr-2 h-4 w-4" />
                    Approve and schedule
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </section>

        <footer className="space-y-3 border-t bg-stone-50 px-8 py-4">
          <a
            href={publicReportPdfUrl(token, search.email)}
            className="inline-flex items-center text-sm font-medium hover:underline"
            style={{ color: 'var(--rpt-primary)' }}
          >
            <Download className="mr-2 h-4 w-4" />
            Download report PDF
          </a>
          <p className="text-xs text-muted-foreground">
            Have a question about this report?{' '}
            <a
              href={`mailto:?subject=Question about report ${data.report.number}`}
              className="underline hover:text-foreground"
            >
              Email us
            </a>
            .
          </p>
        </footer>
      </main>
    </div>
  );
}
